import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Truck, ArrowRight, CheckCircle, Loader2, Shield, Users, Zap } from "lucide-react";

const formSchema = z.object({
  first_name: z.string().min(2, "Le prénom doit contenir au moins 2 caractères").max(50),
  last_name: z.string().min(2, "Le nom doit contenir au moins 2 caractères").max(50),
  email: z.string().email("Email invalide").max(255),
  phone: z.string().min(10, "Numéro de téléphone invalide").max(20),
  company_name: z.string().max(100).optional(),
  siret: z.string().max(20).optional(),
  vehicle_type: z.string().min(1, "Type de véhicule requis"),
  coverage_zones: z.string().min(1, "Zones de couverture requises"),
  experience_years: z.string().optional(),
  has_transport_license: z.boolean().default(false),
  has_insurance: z.boolean().default(false),
  cgu_accepted: z.boolean().refine((val) => val === true, {
    message: "Vous devez accepter les CGU",
  }),
  notes: z.string().max(1000).optional(),
});

type FormData = z.infer<typeof formSchema>;

const benefits = [
  {
    icon: Users,
    title: "Demandes qualifiées",
    description: "Recevez des demandes de clients prêts à envoyer",
  },
  {
    icon: Zap,
    title: "Zéro prospection",
    description: "Les clients viennent directement à vous",
  },
  {
    icon: Shield,
    title: "Inscription gratuite",
    description: "Payez uniquement si vous acceptez une mission",
  },
];

const CarrierApplication = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      has_transport_license: false,
      has_insurance: false,
      cgu_accepted: false,
    },
  });

  const cguAccepted = watch("cgu_accepted");
  const hasLicense = watch("has_transport_license");
  const hasInsurance = watch("has_insurance");

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("carrier_applications").insert({
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        company_name: data.company_name || null,
        siret: data.siret || null,
        vehicle_type: data.vehicle_type,
        coverage_zones: data.coverage_zones,
        experience_years: data.experience_years || null,
        has_transport_license: data.has_transport_license,
        has_insurance: data.has_insurance,
        cgu_accepted: data.cgu_accepted,
        notes: data.notes || null,
        user_id: null,
      });

      if (error) {
        throw error;
      }

      setIsSuccess(true);
      toast({
        title: "Candidature envoyée !",
        description: "Nous allons étudier votre dossier et vous recontacter rapidement.",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 section-padding">
          <div className="container mx-auto max-w-2xl text-center">
            <div className="bg-card border border-border rounded-3xl p-8 md:p-12">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent/10 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-accent" />
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                Candidature reçue !
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Merci pour votre candidature. Notre équipe va étudier votre dossier et vous recontactera
                sous 48h pour la suite du processus de vérification.
              </p>
              <Button onClick={() => navigate("/")} className="bg-accent text-accent-foreground hover:bg-accent/90">
                Retour à l'accueil
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
      <Header />
      <main className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl w-full">
          <div className="grid lg:grid-cols-5 gap-6 lg:gap-12 items-start">
            {/* Left - Benefits */}
            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-28">
                <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Truck className="w-4 h-4" />
                  Devenir partenaire
                </div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                  Rejoignez le réseau GoBledPro
                </h1>
                <p className="text-muted-foreground mb-8">
                  Inscrivez-vous gratuitement et recevez des demandes de transport qualifiées
                  pour vos trajets France-Maroc.
                </p>

                <div className="space-y-6">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                        <benefit.icon className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-foreground mb-1">
                          {benefit.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">{benefit.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className="lg:col-span-3 min-w-0 w-full">
              <form onSubmit={handleSubmit(onSubmit)} className="bg-card border border-border rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8">
                {/* Identity */}
                <div className="mb-8">
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-4 pb-2 border-b border-border">
                    Votre identité
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">Prénom *</Label>
                      <Input
                        id="first_name"
                        placeholder="Mohamed"
                        {...register("first_name")}
                        className={errors.first_name ? "border-destructive" : ""}
                      />
                      {errors.first_name && (
                        <p className="text-sm text-destructive">{errors.first_name.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Nom *</Label>
                      <Input
                        id="last_name"
                        placeholder="Karimi"
                        {...register("last_name")}
                        className={errors.last_name ? "border-destructive" : ""}
                      />
                      {errors.last_name && (
                        <p className="text-sm text-destructive">{errors.last_name.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="mohamed@email.com"
                        {...register("email")}
                        className={errors.email ? "border-destructive" : ""}
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">{errors.email.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone *</Label>
                      <Input
                        id="phone"
                        placeholder="+33 6 12 34 56 78"
                        {...register("phone")}
                        className={errors.phone ? "border-destructive" : ""}
                      />
                      {errors.phone && (
                        <p className="text-sm text-destructive">{errors.phone.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Company */}
                <div className="mb-8">
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-4 pb-2 border-b border-border">
                    Votre entreprise (optionnel)
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Nom de l'entreprise</Label>
                      <Input
                        id="company_name"
                        placeholder="Transport Karimi SARL"
                        {...register("company_name")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="siret">Numéro SIRET</Label>
                      <Input
                        id="siret"
                        placeholder="123 456 789 00012"
                        {...register("siret")}
                      />
                    </div>
                  </div>
                </div>

                {/* Activity */}
                <div className="mb-8">
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-4 pb-2 border-b border-border">
                    Votre activité
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="vehicle_type">Type de véhicule *</Label>
                      <Select onValueChange={(value) => setValue("vehicle_type", value)}>
                        <SelectTrigger className={errors.vehicle_type ? "border-destructive" : ""}>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="utilitaire">Utilitaire</SelectItem>
                          <SelectItem value="camionnette">Camionnette</SelectItem>
                          <SelectItem value="camion">Camion</SelectItem>
                          <SelectItem value="semi-remorque">Semi-remorque</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.vehicle_type && (
                        <p className="text-sm text-destructive">{errors.vehicle_type.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="experience_years">Années d'expérience</Label>
                      <Select onValueChange={(value) => setValue("experience_years", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="moins-2">Moins de 2 ans</SelectItem>
                          <SelectItem value="2-5">2 à 5 ans</SelectItem>
                          <SelectItem value="5-10">5 à 10 ans</SelectItem>
                          <SelectItem value="plus-10">Plus de 10 ans</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="coverage_zones">Zones de couverture *</Label>
                      <Input
                        id="coverage_zones"
                        placeholder="Ex: Île-de-France, Lyon, Marseille → Casablanca, Rabat, Fès"
                        {...register("coverage_zones")}
                        className={errors.coverage_zones ? "border-destructive" : ""}
                      />
                      {errors.coverage_zones && (
                        <p className="text-sm text-destructive">{errors.coverage_zones.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Documents */}
                <div className="mb-8">
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-4 pb-2 border-b border-border">
                    Documents
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="has_transport_license"
                        checked={hasLicense}
                        onCheckedChange={(checked) => setValue("has_transport_license", checked as boolean)}
                      />
                      <Label htmlFor="has_transport_license" className="cursor-pointer">
                        Je possède une licence de transport
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="has_insurance"
                        checked={hasInsurance}
                        onCheckedChange={(checked) => setValue("has_insurance", checked as boolean)}
                      />
                      <Label htmlFor="has_insurance" className="cursor-pointer">
                        Je possède une assurance marchandises transportées
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="mb-8">
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-4 pb-2 border-b border-border">
                    Informations complémentaires
                  </h2>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes (optionnel)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Parlez-nous de votre expérience, vos spécialités, etc."
                      {...register("notes")}
                      rows={4}
                    />
                  </div>
                </div>

                {/* CGU */}
                <div className="bg-muted/50 rounded-xl p-4 mb-8">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="cgu_accepted"
                      checked={cguAccepted}
                      onCheckedChange={(checked) => setValue("cgu_accepted", checked as boolean)}
                      className={errors.cgu_accepted ? "border-destructive" : ""}
                    />
                    <Label htmlFor="cgu_accepted" className="cursor-pointer text-sm">
                      J'accepte les{" "}
                      <Link to="/cgu" className="underline hover:text-accent">
                        Conditions Générales d'Utilisation
                      </Link>{" "}
                      et je confirme que les informations fournies sont exactes. *
                    </Label>
                  </div>
                  {errors.cgu_accepted && (
                    <p className="text-sm text-destructive mt-2">{errors.cgu_accepted.message}</p>
                  )}
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-accent text-accent-foreground hover:bg-accent/90 text-lg py-6 font-semibold"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Truck className="w-5 h-5 mr-2" />
                      Envoyer ma candidature
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CarrierApplication;
